/**
 * What the service says about a plan, rendered against the step it is about.
 *
 * Two different things, deliberately not merged. A **suggestion** is a detail
 * the compiler filled in because the description did not say — the author rules
 * on it, and until they do the draft cannot be published. A **violation** is a
 * rule the plan breaks: there is nothing to accept, the plan has to change.
 *
 * Both are written to be read beside their step ("Nothing reaches this step."),
 * so they carry no step number and only make sense in place.
 */

import { ExclamationCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import { violationMessage } from './utils/AOPViolationUtils';
import { Button } from 'antd';
import React, { FC } from 'react';
import { AOPSuggestion, AOPViolation } from '../../types/AOPTypes';

/**
 * Each rule in the words an author would use, rather than its name.
 *
 * The service adds rules — `unknown_placeholder` and `past_time_limit` arrived
 * after the first eight — and a rule this build has not seen used to render a
 * blank heading above its message. `violationLabel` humanises the rule name
 * instead, so a new one reads as "Past time limit" until it is given better
 * words here.
 */
const VIOLATION_LABELS: { [rule: string]: string } = {
	unsupported_step: 'Not executable',
	missing_exit: 'Missing branch',
	extra_exit: 'Branch that cannot be taken',
	dangling_exit: 'Branch goes nowhere',
	unreachable_step: 'Nothing reaches this step',
	dead_end: 'Dead end',
	false_success: 'Ends as a success when it is not',
	duplicate_step: 'Duplicate step',
	unknown_placeholder: 'Unknown reference',
	past_time_limit: 'Waits longer than allowed',
};

const violationLabel = (rule: string): string => {
	if (VIOLATION_LABELS[rule]) {
		return VIOLATION_LABELS[rule];
	}
	const words = String(rule).split('_').join(' ');
	return words.charAt(0).toUpperCase() + words.slice(1);
};

export const AOPViolationNote: FC<{ violation: AOPViolation; compact?: boolean }> = ({
	violation,
	compact,
}) => (
	<div className="flex items-start gap-xs rounded border border-solid border-grey-4 bg-grey-1 p-xs">
		<span className="text-character-danger">
			<ExclamationCircleFilled />
		</span>
		<span className="flex min-w-0 flex-col gap-xxs">
			<span className={compact ? 'footnoteregular' : 'bodyregular'}>
				{violationLabel(violation.rule)}
			</span>
			<span className={`${compact ? 'footnoteregular' : 'bodyregular'} text-character-secondary`}>
				{violationMessage(violation)}
			</span>
		</span>
	</div>
);

interface AOPSuggestionNoteProps {
	suggestion: AOPSuggestion;
	compact?: boolean;
	/** Omitted where a suggestion can be read but not ruled on. */
	onAccept?: () => void;
	/**
	 * Declining the compiler's filled-in detail means dropping the step it is
	 * on — a suggestion has no "rejected" state, only accepted or gone with its
	 * step.
	 */
	onDecline?: () => void;
	isSaving?: boolean;
}

export const AOPSuggestionNote: FC<AOPSuggestionNoteProps> = ({
	suggestion,
	compact,
	onAccept,
	onDecline,
	isSaving,
}) => (
	<div className="flex items-start gap-xs rounded border border-solid border-grey-4 bg-grey-1 p-xs">
		<span className="text-purple-6">
			<InfoCircleFilled />
		</span>
		<div className="flex min-w-0 flex-1 flex-col gap-2xs">
			<span className={`${compact ? 'footnoteregular' : 'bodyregular'} text-character-primary`}>
				<span className={compact ? 'footnoteregular' : 'bodyregular'}>Auto-filled by Radar: </span>
				{suggestion.message}
			</span>

			{suggestion.status === 'open' && (onAccept || onDecline) ? (
				<div className="flex items-center gap-xs">
					{onDecline ? (
						<Button
							size="small"
							className="aop-sentence-case"
							disabled={isSaving}
							onClick={onDecline}
						>
							Cancel
						</Button>
					) : null}
					{onAccept ? (
						<Button
							size="small"
							className="aop-sentence-case"
							type="primary"
							loading={isSaving}
							onClick={onAccept}
						>
							Approve
						</Button>
					) : null}
				</div>
			) : null}

			{suggestion.status === 'accepted' ? (
				<span className="footnoteregular text-character-secondary">Approved</span>
			) : null}
		</div>
	</div>
);

/** Suggestions and violations about the plan as a whole rather than one step. */
export const unanchoredSuggestions = (suggestions: AOPSuggestion[]): AOPSuggestion[] =>
	suggestions.filter((suggestion) => !suggestion.anchor && suggestion.status !== 'removed');

export const unanchoredViolations = (violations: AOPViolation[]): AOPViolation[] =>
	violations.filter((violation) => !violation.anchor);

export const suggestionsFor = (suggestions: AOPSuggestion[], stepId: string): AOPSuggestion[] =>
	suggestions.filter(
		(suggestion) => suggestion.anchor === stepId && suggestion.status !== 'removed'
	);

export const violationsFor = (violations: AOPViolation[], stepId: string): AOPViolation[] =>
	violations.filter((violation) => violation.anchor === stepId);
